import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDownward, ArrowUpward, Block } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { Cell } from '@tanstack/react-table'

import { BIND_TYPE } from '@constants'
import { FormattedColumnRestrictions, RESTRICTION_TYPE, RestrictionDTO } from '@types'

interface SortCellProps {
  cell: Cell<FormattedColumnRestrictions, unknown>
  updateRestrictions: (restrictions: RestrictionDTO[]) => Promise<unknown>
  viewId: number
  restrictionsDataRows: FormattedColumnRestrictions[]
}

export const SortCell: FC<SortCellProps> = ({
  cell,
  updateRestrictions,
  viewId,
  restrictionsDataRows,
}) => {
  const { t } = useTranslation()
  const columnRestrictions = cell.row.original

  // Функция определения следующего состояния сортировки
  const getNextSortState = (
    currentState: string | undefined,
    reverse = false
  ): string | undefined => {
    // Порядок состояний
    const states = ['undefined', 'desc', 'asc']

    // Определяем текущий индекс
    let currentIndex = states.indexOf(currentState || 'undefined')

    // Если состояние не найдено, считаем его undefined
    if (currentIndex === -1) currentIndex = 0

    // Получаем следующий индекс в зависимости от направления
    let nextIndex
    if (reverse) {
      // При правом клике - движение налево
      nextIndex = (currentIndex - 1 + states.length) % states.length
    } else {
      // При левом клике - движение вправо
      nextIndex = (currentIndex + 1) % states.length
    }

    // Возвращаем новое состояние или undefined для первого элемента
    return states[nextIndex] === 'undefined' ? undefined : states[nextIndex]
  }

  // Обработчик левого клика - движение вперед
  const handleLeftClick = () => {
    const newDirection = getNextSortState(columnRestrictions.sort?.sortDirection)
    applyNewSortDirection(newDirection)
  }

  // Обработчик правого клика - движение назад
  const handleRightClick = (event: React.MouseEvent) => {
    event.preventDefault() // Предотвращаем стандартное контекстное меню

    const newDirection = getNextSortState(columnRestrictions.sort?.sortDirection, true)
    applyNewSortDirection(newDirection)
  }

  // Функция применения нового направления сортировки
  const applyNewSortDirection = (newDirection: string | undefined) => {
    const priority: number =
      columnRestrictions.sort?.priority !== undefined
        ? columnRestrictions.sort.priority
        : restrictionsDataRows.filter(row => row.sort?.sortDirection).length || 0

    const columnToSort = columnRestrictions.sort?.columnToSort || columnRestrictions.fieldCode

    if (newDirection === undefined) {
      // Удаление сортировки и обновление приоритета у других колонок
      updateRestrictions([
        ...restrictionsDataRows
          .filter(columnR => columnR.sort?.priority > (columnRestrictions.sort?.priority || 0))
          .map(col => ({
            viewId,
            fieldCode: col.fieldCode,
            fieldName: col.fieldName,
            restrictionType: RESTRICTION_TYPE.SORT,
            value: `${col.sort?.columnToSort}//${col.sort?.sortDirection}//${
              col.sort?.priority - 1
            }`,
          })),
        {
          viewId,
          fieldCode: columnRestrictions.fieldCode,
          fieldName: columnRestrictions.fieldName,
          restrictionType: RESTRICTION_TYPE.SORT,
          value: undefined,
        },
      ])
    } else {
      // Установка или изменение сортировки
      updateRestrictions([
        {
          viewId,
          fieldCode: columnRestrictions.fieldCode,
          fieldName: columnRestrictions.fieldName,
          restrictionType: RESTRICTION_TYPE.SORT,
          value: `${columnToSort}//${newDirection}//${priority}`,
        },
      ])
    }
  }

  // Обработчики для изменения приоритета
  const handlePriorityLeftClick = (event: React.MouseEvent) => {
    event.stopPropagation() // Предотвращаем всплытие события к родительскому обработчику

    if (columnRestrictions.sort?.sortDirection) {
      const currentPriority = Number(columnRestrictions.sort.priority)
      const maxPriority = restrictionsDataRows.filter(row => row.sort?.sortDirection).length - 1

      // Определяем новый приоритет, увеличивая текущий, но не превышая максимальный
      const newPriority = Math.min(currentPriority + 1, maxPriority)

      // Если новый приоритет отличается от текущего, применяем изменения
      if (newPriority !== currentPriority) {
        applyNewPriority(newPriority, currentPriority)
      }
    }
  }

  const handlePriorityRightClick = (event: React.MouseEvent) => {
    event.preventDefault() // Предотвращаем стандартное контекстное меню
    event.stopPropagation() // Предотвращаем всплытие события к родительскому обработчику

    if (columnRestrictions.sort?.sortDirection) {
      const currentPriority = Number(columnRestrictions.sort.priority)

      // Определяем новый приоритет, уменьшая текущий, но не меньше 0
      const newPriority = Math.max(currentPriority - 1, 0)

      // Если новый приоритет отличается от текущего, применяем изменения
      if (newPriority !== currentPriority) {
        applyNewPriority(newPriority, currentPriority)
      }
    }
  }

  // Функция применения нового приоритета
  const applyNewPriority = (newPriority: number, currentPriority: number) => {
    const columnToSort = columnRestrictions.sort?.columnToSort || columnRestrictions.fieldCode
    const direction = columnRestrictions.sort?.sortDirection

    // Собираем все обновления, которые нужно применить
    const updates = []

    // Обновляем текущую колонку
    updates.push({
      viewId,
      fieldCode: columnRestrictions.fieldCode,
      fieldName: columnRestrictions.fieldName,
      restrictionType: RESTRICTION_TYPE.SORT,
      value: `${columnToSort}//${direction}//${newPriority}`,
    })

    // Обновляем все затронутые колонки
    if (newPriority > currentPriority) {
      // Если приоритет увеличился, уменьшаем приоритет у колонок между старым и новым значением
      updates.push(
        ...restrictionsDataRows
          .filter(
            col =>
              col.sort?.priority !== undefined &&
              col.sort.priority > currentPriority &&
              col.sort.priority <= newPriority &&
              col.fieldCode !== columnRestrictions.fieldCode
          )
          .map(col => ({
            viewId,
            fieldCode: col.fieldCode,
            fieldName: col.fieldName,
            restrictionType: RESTRICTION_TYPE.SORT,
            value: `${col.sort?.columnToSort}//${col.sort?.sortDirection}//${
              col.sort.priority - 1
            }`,
          }))
      )
    } else {
      // Если приоритет уменьшился, увеличиваем приоритет у колонок между новым и старым значением
      updates.push(
        ...restrictionsDataRows
          .filter(
            col =>
              col.sort?.priority !== undefined &&
              col.sort.priority >= newPriority &&
              col.sort.priority < currentPriority &&
              col.fieldCode !== columnRestrictions.fieldCode
          )
          .map(col => ({
            viewId,
            fieldCode: col.fieldCode,
            fieldName: col.fieldName,
            restrictionType: RESTRICTION_TYPE.SORT,
            value: `${col.sort?.columnToSort}//${col.sort?.sortDirection}//${
              Number(col.sort.priority) + 1
            }`,
          }))
      )
    }

    // Применяем все обновления
    updateRestrictions(updates)
  }

  // Определение иконки на основе текущего направления сортировки
  let SortIcon = Block
  let iconColor = 'disabled'

  if (columnRestrictions.sort?.sortDirection === 'asc') {
    SortIcon = ArrowUpward
    iconColor = 'primary'
  } else if (columnRestrictions.sort?.sortDirection === 'desc') {
    SortIcon = ArrowDownward
    iconColor = 'primary'
  }

  return (
    <>
      <IconButton
        size={'small'}
        disabled={[BIND_TYPE.JS, BIND_TYPE.JSX, BIND_TYPE.ACTION].includes(
          columnRestrictions.bindType as BIND_TYPE
        )}
        onClick={handleLeftClick}
        onContextMenu={handleRightClick}
      >
        <SortIcon color={iconColor as any} />
      </IconButton>
      {columnRestrictions.sort?.priority !== undefined &&
        columnRestrictions.sort?.sortDirection && (
          <IconButton
            sx={{ ml: 0.5, fontSize: '0.75rem', width: 24, height: 24 }}
            onClick={handlePriorityLeftClick}
            onContextMenu={handlePriorityRightClick}
          >
            {Number(columnRestrictions.sort.priority) + 1}
          </IconButton>
        )}
    </>
  )
}
